import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../redux/store';
import { getErrorMessage } from '../utils/commonUtils';
import {
    setEcwOrderList,
    setEcwOrderLogs
} from '../redux/ecwOrder/ecwOrderSlice';
import {
    useCreateEcwPatientFromFhirMutation,
    useGetAllEcwOrdersByPatientIdMutation,
    useGetEcwOrderLogsMutation,
    useGetEcwOrdersMutation,
    useGetEcwPatientsMutation,
    useRecordEcwOrderMutation,
    useRevertEcwOrderMutation,
    useSaveAllEcwOrdersForPatientMutation,
    useGetAllEncountersForPatientMutation,
    useSaveAllEcwOrdersByEncounterFromFhirMutation,
    useValidateManufacturerWithEcwMutation,
} from '../redux/ecwOrder/ecwOrderApiSlice';
import { toast } from 'react-toastify';

const useEcwOrders = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [getEcwOrders, { isLoading: getEcwOrdersLoading }] = useGetEcwOrdersMutation();
    const [recordEcwOrder, { isLoading: recordEcwOrderLoading }] = useRecordEcwOrderMutation();
    const [getEcwOrderLogs, { isLoading: getEcwOrderLogsLoading }] = useGetEcwOrderLogsMutation();
    const [revertEcwOrder, { isLoading: revertEcwOrderLoading }] = useRevertEcwOrderMutation();
    const [getEcwPatients, { isLoading: getEcwPatientsLoading }] = useGetEcwPatientsMutation();
    const [createEcwPatientFromFhir, { isLoading: createEcwPatientFromFhirLoading }] = useCreateEcwPatientFromFhirMutation();
    const [getAllEcwOrdersByPatientId, { isLoading: getAllEcwOrdersByPatientIdLoading }] = useGetAllEcwOrdersByPatientIdMutation();
    const [saveAllEcwOrdersForPatient, { isLoading: saveAllEcwOrdersForPatientLoading }] = useSaveAllEcwOrdersForPatientMutation();
    const [getAllEncountersForPatient, { isLoading: getAllEncountersForPatientLoading }] = useGetAllEncountersForPatientMutation();
    const [saveAllEcwOrdersByEncounterFromFhir, { isLoading: saveAllEcwOrdersByEncounterFromFhirLoading }] = useSaveAllEcwOrdersByEncounterFromFhirMutation();
    const [validateManufacturerWithEcw, { isLoading: validateManufacturerWithEcwLoading }] = useValidateManufacturerWithEcwMutation();

    const getEcwOrdersAPI = useCallback(async (data: Object) => {
        try {
            const response = await getEcwOrders(data).unwrap();
            if (response?.result) {
                dispatch(setEcwOrderList(response.result));
                return response.result;
            }
            return response;
        } catch (err) {
            const message = getErrorMessage(err);
            throw new Error(message);
        }
    }, [getEcwOrders, dispatch]);

    const recordEcwOrderAPI = useCallback(async (data: Object) => {
        try {
            const response = await recordEcwOrder(data).unwrap();
            return response;
        } catch (err) {
            const message = getErrorMessage(err);
            throw new Error(message);
        }
    }, [recordEcwOrder]);

    const getEcwOrderLogsAPI = useCallback(async (data: Object) => {
        try {
            const response = await getEcwOrderLogs(data).unwrap();
            if (response?.result) {
                dispatch(setEcwOrderLogs(response.result));
                return response.result;
            }
            return response;
        } catch (err) {
            const message = getErrorMessage(err);
            throw new Error(message);
        }
    }, [getEcwOrderLogs]);

    const revertEcwOrderAPI = useCallback(async (data: Object) => {
        try {
            const response = await revertEcwOrder(data).unwrap();
            toast.success(response?.message || "Order reverted successfully");
            return response;
        } catch (err) {
            const message = getErrorMessage(err);
            throw new Error(message);
        }
    }, [revertEcwOrder]);

    const getEcwPatientsAPI = useCallback(async (data: Object) => {
        try {
            const response = await getEcwPatients(data).unwrap();
            return response?.result || response;
        } catch (err) {
            const message = getErrorMessage(err);
            throw new Error(message);
        }
    }, [getEcwPatients]);

    const createEcwPatientFromFhirAPI = useCallback(async (data: Object) => {
        try {
            const response = await createEcwPatientFromFhir(data).unwrap();
            return response;
        } catch (err) {
            const message = getErrorMessage(err);
            throw new Error(message);
        }
    }, [createEcwPatientFromFhir]);

    const getAllEcwOrdersByPatientIdAPI = useCallback(async (data: Object) => {
        try {
            const response = await getAllEcwOrdersByPatientId(data).unwrap();
            return response?.result || response;
        } catch (err) {
            const message = getErrorMessage(err);
            throw new Error(message);
        }
    }, [getAllEcwOrdersByPatientId]);

    const saveAllEcwOrdersForPatientAPI = useCallback(async (data: Object) => {
        try {
            const response = await saveAllEcwOrdersForPatient(data).unwrap();
            return response;
        } catch (err) {
            const message = getErrorMessage(err);
            throw new Error(message);
        }
    }, [saveAllEcwOrdersForPatient]);

    const getAllEncountersForPatientAPI = useCallback(async (data: Object) => {
        try {
            const response = await getAllEncountersForPatient(data).unwrap();
            return response?.result;
        } catch (err) {
            const message = getErrorMessage(err);
            throw new Error(message);
        }
    }, [getAllEncountersForPatient]);

    const saveAllEcwOrdersByEncounterFromFhirAPI = useCallback(async (data: { patientId: string; encounter: any }) => {
        try {
            const response = await saveAllEcwOrdersByEncounterFromFhir(data).unwrap();
            return response;
        } catch (err) {
            const message = getErrorMessage(err);
            throw new Error(message);
        }
    }, [saveAllEcwOrdersByEncounterFromFhir]);

    const validateManufacturerWithEcwAPI = useCallback(async (data: any) => {
        try {
            const response = await validateManufacturerWithEcw(data).unwrap();
            return response;
        } catch (err) {
            const message = getErrorMessage(err);
            throw new Error(message);
        }
    }, [validateManufacturerWithEcw]);

    return {
        getEcwOrdersAPI,
        getEcwOrdersLoading,
        recordEcwOrderAPI,
        recordEcwOrderLoading,
        getEcwOrderLogsAPI,
        getEcwOrderLogsLoading,
        revertEcwOrderAPI,
        revertEcwOrderLoading,
        getEcwPatientsAPI,
        getEcwPatientsLoading,
        createEcwPatientFromFhirAPI,
        createEcwPatientFromFhirLoading,
        getAllEcwOrdersByPatientIdAPI,
        getAllEcwOrdersByPatientIdLoading,
        saveAllEcwOrdersForPatientAPI,
        saveAllEcwOrdersForPatientLoading,
        getAllEncountersForPatientAPI,
        getAllEncountersForPatientLoading,
        saveAllEcwOrdersByEncounterFromFhirAPI,
        saveAllEcwOrdersByEncounterFromFhirLoading,
        validateManufacturerWithEcwAPI,
        validateManufacturerWithEcwLoading,
    };
};

export default useEcwOrders;
